<template>
  <el-dialog
    :title="$t('billingPage.PostageRechargeManualMatchTitle')"
    :visible.sync="dialogVisible"
    width="860px"
    destroy-on-close
    append-to-body
    :before-close="hide"
  >
    <div class="manual-match">
      <div class="manual-match__target">
        <div class="manual-match__target-title">{{ $t('billingPage.PostageRechargeTargetItem') }}</div>
        <el-row :gutter="20">
          <el-col :span="8">
            <div class="manual-match__label">{{ $t('billingPage.consignmentNum') }}</div>
            <div class="manual-match__value">{{ target.consignmentNum }}</div>
          </el-col>
          <el-col :span="8">
            <div class="manual-match__label">{{ $t('billingPage.PostageRechargeInvoicePrice') }}</div>
            <div class="manual-match__value">{{ formatMoney(target.invoicePrice) }}</div>
          </el-col>
          <el-col :span="8">
            <div class="manual-match__label">{{ $t('billingPage.PostageRechargeRawData') }}</div>
            <div
              class="manual-match__value is-raw"
              :title="formatRawData(getRawData(target))"
              @click="copyRawData(getRawData(target))"
            >
              {{ formatRawData(getRawData(target)) }}
            </div>
          </el-col>
        </el-row>
      </div>

      <el-form label-position="top" class="manual-match__search">
        <el-form-item :label="$t('billingPage.PostageRechargeSearchOrder')">
          <el-input
            v-model.trim="searchKeyword"
            :placeholder="$t('billingPage.PostageRechargeSearchOrderPlaceholder')"
            clearable
            @keyup.enter.native="searchOrders"
          >
            <el-button slot="append" icon="el-icon-search" :loading="searchLoading" @click="searchOrders">
              {{ $t('searchText') }}
            </el-button>
          </el-input>
        </el-form-item>
      </el-form>

      <el-table :data="orderList" border max-height="320px" @row-click="selectRow">
        <el-table-column width="55" align="center">
          <template slot-scope="scope">
            <el-radio v-model="selectedLogisticsNo" :label="scope.row.logisticsNo" @change="selectRow(scope.row)">
              <span></span>
            </el-radio>
          </template>
        </el-table-column>
        <el-table-column prop="orderId" :label="$t('billingPage.PostageRechargeOrderNumber')" align="center" min-width="180" />
        <el-table-column prop="clientName" :label="$t('billingPage.CustomerName')" align="center" min-width="180" />
        <el-table-column prop="logisticsNo" :label="$t('billingPage.consignmentNum')" align="center" min-width="180" />
      </el-table>
    </div>
    <span slot="footer" class="dialog-footer">
      <span v-if="selectedRow" class="manual-match__current">
        {{ $t('billingPage.PostageRechargeCurrentSelection') }}:
        {{ selectedRow.orderId }} / {{ selectedRow.logisticsNo }}
      </span>
      <el-button @click="hide">{{ $t('cancelText') }}</el-button>
      <el-button type="primary" :loading="confirmLoading" :disabled="!selectedRow" @click="confirm">
        {{ $t('billingPage.PostageRechargeConfirmMatch') }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import { billApi } from '@/api';

export default {
  name: 'PostageRechargeManualMatchDialog',
  data() {
    return {
      dialogVisible: false,
      searchLoading: false,
      confirmLoading: false,
      target: {},
      searchKeyword: '',
      orderList: [],
      selectedRow: null,
      selectedLogisticsNo: ''
    };
  },
  methods: {
    show(row) {
      this.dialogVisible = true;
      this.target = { ...row };
      this.searchKeyword = '';
      this.orderList = [];
      this.selectedRow = null;
      this.selectedLogisticsNo = '';
      if (!this.getRawData(this.target) && this.target.id) {
        this.getTargetDetail();
      }
    },
    hide() {
      this.dialogVisible = false;
      this.target = {};
      this.searchKeyword = '';
      this.orderList = [];
      this.selectedRow = null;
      this.selectedLogisticsNo = '';
    },
    searchOrders() {
      if (!this.searchKeyword) {
        this.$message.warning(this.$t('billingPage.PostageRechargeSearchOrderRequired'));
        return;
      }
      this.searchLoading = true;
      billApi.getPostageRechargeErpOrderList({ orderNumber: this.searchKeyword }).then(res => {
        this.orderList = res.data || [];
        this.selectedRow = null;
        this.selectedLogisticsNo = '';
      }).finally(() => {
        this.searchLoading = false;
      });
    },
    getTargetDetail() {
      billApi.getPostageRechargeItemById({ id: this.target.id }).then(res => {
        this.target = {
          ...this.target,
          ...(res.data || {})
        };
      });
    },
    selectRow(row) {
      this.selectedRow = row;
      this.selectedLogisticsNo = row.logisticsNo;
    },
    confirm() {
      if (!this.target.id || !this.selectedRow || !this.selectedRow.logisticsNo) {
        this.$message.warning(this.$t('billingPage.PostageRechargeMatchRequired'));
        return;
      }
      this.confirmLoading = true;
      billApi.matchPostageRechargeOrderManually({
        id: this.target.id,
        logisticsNo: this.selectedRow.logisticsNo
      }).then(res => {
        if (res.code == 200 || !res.failed) {
          this.msgSuccess(this.$t('toast.operationSuccessText'));
          this.hide();
          this.$emit('matched');
        }
      }).finally(() => {
        this.confirmLoading = false;
      });
    },
    formatMoney(value) {
      const number = Number(value || 0);
      return `$ ${number.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    getRawData(row) {
      return row.originalCsvRawData || row.originalCsvData;
    },
    formatRawData(value) {
      if (!value) return '';
      if (typeof value === 'string') return value;
      return Object.keys(value).map(key => `${key}: ${value[key]}`).join(' | ');
    },
    copyRawData(value) {
      const text = this.formatRawData(value);
      if (!text) return;
      this.$copyText(text).then(() => {
        this.msgSuccess(this.$t('ReplicationSucceededText'));
      }, () => {
        this.msgError(this.$t('ReplicationFailedText') || 'Copy failed');
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.manual-match {
  &__target {
    padding: 16px;
    border-radius: 6px;
    border-left: 4px solid #0056d2;
    background: #f2f6ff;
  }

  &__target-title {
    margin-bottom: 12px;
    color: #0056d2;
    font-size: 12px;
    font-weight: 700;
  }

  &__label {
    color: #606266;
    font-size: 12px;
    font-weight: 600;
  }

  &__value {
    margin-top: 6px;
    color: #143161;
    font-size: 16px;
    font-weight: 600;
    word-break: break-all;

    &.is-raw {
      display: block;
      width: 100%;
      overflow: hidden;
      font-size: 13px;
      font-weight: 400;
      text-overflow: ellipsis;
      white-space: nowrap;
      cursor: pointer;
    }
  }

  &__search {
    margin-top: 18px;
  }

  &__current {
    float: left;
    color: #606266;
    line-height: 28px;
  }
}
</style>
